// Rewards bindings. Mirrors the iOS RewardsStore surface against /rewards/*.
// The unlock "tank" is server-authoritative: earned_minutes_total is the sum of
// every attempt's whole-minute credit. /rewards/me is the effort-aware earn-rate
// table the nightly job feeds (effective_minutes_per_correct already folds in the
// population multiplier + the learned/curated effort weight).
import { api } from "./client";

export interface RewardBalance {
  earned_minutes_total: number;
}

/** Lifetime minutes earned across all devices (legacy; kept for old callers). */
export function getBalance(): Promise<RewardBalance> {
  return api.get<RewardBalance>("/rewards/balance");
}

/**
 * Current spendable unlock balance — the server-authoritative shared "tank".
 * Earn on any device (incl. web) credits it; iOS unlocks debit it. Web can't
 * spend it, but shows the same number the phone/iPad tank displays.
 */
export interface RewardWallet {
  balance_minutes: number;
  applied?: boolean;
  /** Lifetime minutes earned across all devices (stats). Optional — absent on
   * older servers. */
  lifetime_earned?: number;
  /** Lifetime minutes spent on unlocks (excludes cadence resets). */
  lifetime_spent?: number;
}

export function getWallet(): Promise<RewardWallet> {
  return api.get<RewardWallet>("/rewards/wallet");
}

/** One per-(framework, question_type) effective earn-rate row from /rewards/me. */
export interface EffectiveRewardRow {
  framework_slug: string;
  question_type: string;
  multiplier: number;
  effort_weight: number;
  /** round(base × multiplier × effort) — the realistic per-correct minutes. */
  effective_minutes_per_correct: number;
}

export interface RewardsMe {
  base_minutes_per_correct: number;
  base_minutes_per_set: number;
  per_type: EffectiveRewardRow[];
}

export function getRewardsMe(): Promise<RewardsMe> {
  return api.get<RewardsMe>("/rewards/me");
}

/**
 * Send unlock-minutes to a friend. Debits the caller, credits the recipient
 * (friends-only, instant, irreversible). Returns the SENDER's new balance;
 * `applied:false` means insufficient balance (re-clamp to `balance_minutes`).
 */
export interface TransferResult {
  balance_minutes: number;
  applied?: boolean;
  transfer_id?: string | null;
}

export function transferMinutes(
  recipientUserId: string,
  minutes: number,
  clientRef: string,
): Promise<TransferResult> {
  return api.post<TransferResult>("/rewards/transfer", {
    recipient_user_id: recipientUserId,
    minutes,
    client_ref: clientRef,
  });
}

/** One row of the caller's transfer history (sent + received). */
export interface TransferHistoryItem {
  transfer_id: string | null;
  direction: "in" | "out";
  minutes: number;
  peer_user_id: string | null;
  peer_username?: string | null;
  peer_display_name: string;
  created_at: string;
}

export function getTransfers(): Promise<TransferHistoryItem[]> {
  return api.get<TransferHistoryItem[]>("/rewards/transfers");
}
